import React from "react";

import { formatFractionDisplay, getFarmFootprintingCapabilities, useGetCarbonProgramByFarmId, useTranslation } from "utils";

import { FARM_FOOTPRINTING_HAS_LAND_CHANGED, FarmFootprintReturnBody } from "models";

import { Card, ListCard } from "components";

import { arableLandDataPairs, electricityDataPairs } from "pages/app/footprinting/components/FootprintingFarmReview";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { FarmElectricityForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmElectricityForm";
import { FarmFuelForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmFuelForm";
import { FarmLandUseForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmLandUseForm";

const HAS_LAND_CHANGED_TO_LABEL = {
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO]: "landUseForm.radioButton.no",
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES]: "landUseForm.radioButton.yes",
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN]: "landUseForm.radioButton.dontKnow",
};

export const FootprintEditFarmDetailsCard = ({
  farmFootprintData,
  isFarmFootprintDataLoading,
  setShowEditModal,
  setFarmModalForm,
}: {
  farmFootprintData: FarmFootprintReturnBody;
  isFarmFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setFarmModalForm: (formType: string) => void;
}) => {
  const { t } = useTranslation("footprintingFarm");
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmFootprintData?.farmId);

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    farmFootprintData.footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setFarmModalForm(formKey);
          },
        };

  return (
    <Card className="admin-edit-farm-details-card padding-125">
      <ReviewPageTitle title={`${t("footprinting:review.farm.title")} - ${farmFootprintData?.year}`} iconName="klim/farm" />
      {/* Farm electricity form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.farm.electricity")}
        titleButton={editButton(FarmElectricityForm.Key)}
        dataPairs={[
          ...electricityDataPairs(t, getFarmFootprintingCapabilities(carbonProgramQuery.data), farmFootprintData?.footprint),
          { label: t("electricityForm.question.renewables"), value: `${formatFractionDisplay(farmFootprintData?.footprint?.renewablesPct ?? 0)} %` },
        ]}
      />
      {/* Farm fuel form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("footprinting:navigation.farm.fuel")}
        titleButton={editButton(FarmFuelForm.Key)}
        dataPairs={[
          { label: t("fuelForm.question.dieselQuestionLabel"), value: `${farmFootprintData?.footprint?.dieselL} L` },
          { label: t("fuelForm.question.petrolQuestionLabel"), value: `${farmFootprintData?.footprint?.petrolL} L` },
          { label: t("fuelForm.question.naturalGasQuestionLabel"), value: `${farmFootprintData?.footprint?.naturalGasM3} m³` },
          { label: t("fuelForm.question.heatingOilQuestionLabel"), value: `${farmFootprintData?.footprint?.heatingOilL} L` },
        ]}
      />
      {/* Land Use Change form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("footprinting:navigation.farm.landuse")}
        titleButton={editButton(FarmLandUseForm.Key)}
        dataPairs={[
          ...arableLandDataPairs(t, farmFootprintData?.footprint),
          {
            label: t("landUseForm.question.general"),
            value: t(HAS_LAND_CHANGED_TO_LABEL[farmFootprintData.footprint?.hasLandChanged ?? FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN]),
            children:
              (farmFootprintData?.footprint?.landUseChangeUsage.length ?? 0) > 0
                ? farmFootprintData?.footprint?.landUseChangeUsage
                    .filter((luc) => luc.hasLandChanged === "yesLUC" && (luc.areaConvertedHa ?? 0) > 0)
                    .map((luc, index) => (
                      <ListCard
                        key={luc.id}
                        isSublist
                        isLoading={isFarmFootprintDataLoading}
                        title={t("landUseForm.question.title") + (index + 1)}
                        dataPairs={[
                          {
                            label: t("landUseForm.question.previousLandChange"),
                            value: luc.previousLandUseType ?? "",
                          },
                          {
                            label: t("landUseForm.question.conversionYear"),
                            value: `${luc.yearOfConversion}`,
                          },
                          {
                            label: t("landUseForm.question.areaConverted"),
                            value: `${luc.areaConvertedHa} Ha`,
                          },
                        ]}
                      />
                    ))
                : null,
          },
        ]}
      />
      {/* Comments */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("commentForm.title")}
        dataPairs={[
          {
            label: t("commentForm.label"),
            value: farmFootprintData?.footprint?.comment || t("commentForm.noComment"),
          },
        ]}
      />
    </Card>
  );
};
